// Inline config form for the op being added. One sub-form per op type; each
// applies by dispatching ADD_OP (which splices it in at the current insert point).
import type { ReactNode } from 'react'
import { useState } from 'react'
import { FRAMES } from '../frames'
import { isDate, schemaAt } from '../pipeline-compile'
import { AggFn, FieldSchema, FrameId, Op, PickableOp } from '../pipeline-types'
import { Action } from './store'
import { Button } from './ui'

interface Props {
  op: PickableOp
  ops: Op[]
  insertAt: number | null
  frame: FrameId
  dispatch: (a: Action) => void
}

const ctl =
  'rounded-md border border-line-form bg-surface px-2 py-1 text-xs text-fg'
const title = 'mb-2 font-mono text-xs text-brand'
const row = 'mb-2 flex flex-wrap items-center gap-2 text-xs text-fg'

function Wrap({ heading, children }: { heading: string; children: ReactNode }) {
  return (
    <div className="mt-3 rounded-lg border border-brand bg-basil-50 p-3">
      <div className={title}>{heading}</div>
      {children}
    </div>
  )
}

function opsForType(t: FieldSchema['type']): string[] {
  return t === 'cat'
    ? ['eq', 'neq', 'in']
    : t === 'date'
      ? ['within_days', 'date_after', 'date_before']
      : ['gte', 'lte']
}

/**
 * Plain-language labels for filter operators — the same friendly wording the
 * flat builder uses (gte → "At least", lte → "At most"), so the op dropdown
 * doesn't surface raw codes like `gte`.
 */
const OP_LABELS: Record<string, string> = {
  eq: 'is',
  neq: 'is not',
  in: 'is any of',
  gte: 'At least',
  lte: 'At most',
  within_days: 'within last',
  date_after: 'on or after',
  date_before: 'on or before',
}
const opLabel = (op: string): string => OP_LABELS[op] ?? op

function FilterForm({
  ops,
  at,
  frame,
  dispatch,
}: {
  ops: Props['ops']
  at: number | null
  frame: FrameId
  dispatch: Props['dispatch']
}) {
  const fields = schemaAt(ops, frame, at ?? undefined)
  const [fieldName, setFieldName] = useState(fields[0]?.name ?? '')
  const field = (fields.find((f) => f.name === fieldName) ?? fields[0])!
  const choices = opsForType(field.type)
  const [op, setOp] = useState(choices[0])
  const eff = choices.includes(op) ? op : choices[0]
  const [single, setSingle] = useState('')
  const [multi, setMulti] = useState<string[]>([])

  const onField = (name: string) => {
    setFieldName(name)
    const f = fields.find((x) => x.name === name)!
    setOp(opsForType(f.type)[0])
    setSingle('')
    setMulti([])
  }

  // The implicit value when the user leaves a select/date untouched. A recency
  // date filter defaults to 90 days; an absolute date bound has no sensible
  // default, so it stays empty (apply() drops empty values).
  const defaultSingle = () =>
    field.type === 'cat'
      ? String(field.values?.[0] ?? '')
      : field.type === 'date' && eff === 'within_days'
        ? '90'
        : ''

  const apply = () => {
    const op = eff as Extract<Op, { type: 'filter' }>['op']
    if (eff === 'in') {
      if (!multi.length) return
      dispatch({
        t: 'ADD_OP',
        op: { type: 'filter', field: field.name, op, value: multi },
      })
    } else {
      const value = single || defaultSingle()
      if (value === '') return
      dispatch({
        t: 'ADD_OP',
        op: { type: 'filter', field: field.name, op, value },
      })
    }
  }

  return (
    <Wrap heading="filter — keep rows where…">
      <div className={row}>
        <select
          value={fieldName}
          onChange={(e) => onField(e.target.value)}
          className={ctl}
        >
          {fields.map((f) => (
            <option key={f.name} value={f.name}>
              {f.name}
            </option>
          ))}
        </select>
        <select
          value={eff}
          onChange={(e) => {
            // A date field's ops carry different value shapes (days number vs an
            // absolute date), so reset the entered value when the op switches.
            setOp(e.target.value)
            setSingle('')
          }}
          className={ctl}
        >
          {choices.map((o) => (
            <option key={o} value={o}>
              {opLabel(o)}
            </option>
          ))}
        </select>
        {field.type === 'cat' && eff === 'in' ? (
          <span className="flex flex-wrap gap-2">
            {(field.values ?? []).map((v) => (
              <label key={String(v)} className="inline-flex items-center gap-1">
                <input
                  type="checkbox"
                  checked={multi.includes(String(v))}
                  onChange={(e) =>
                    setMulti((m) =>
                      e.target.checked
                        ? [...m, String(v)]
                        : m.filter((x) => x !== String(v))
                    )
                  }
                />
                {String(v)}
              </label>
            ))}
          </span>
        ) : field.type === 'cat' ? (
          <select
            value={single || String(field.values?.[0] ?? '')}
            onChange={(e) => setSingle(e.target.value)}
            className={ctl}
          >
            {(field.values ?? []).map((v) => (
              <option key={String(v)} value={String(v)}>
                {String(v)}
              </option>
            ))}
          </select>
        ) : field.type === 'date' && eff === 'within_days' ? (
          <span className="inline-flex items-center gap-1">
            within last
            <input
              type="number"
              placeholder="days"
              value={single || '90'}
              onChange={(e) => setSingle(e.target.value)}
              className={`${ctl} w-24`}
            />
            days
          </span>
        ) : field.type === 'date' ? (
          <input
            type="date"
            aria-label="date"
            value={single}
            onChange={(e) => setSingle(e.target.value)}
            className={`${ctl} w-40`}
          />
        ) : (
          <input
            type="number"
            placeholder="value"
            value={single}
            onChange={(e) => setSingle(e.target.value)}
            className={`${ctl} w-32`}
          />
        )}
      </div>
      <Button variant="primary" onClick={apply}>
        Apply filter
      </Button>
    </Wrap>
  )
}

function GroupForm({
  frame,
  dispatch,
}: {
  frame: FrameId
  dispatch: Props['dispatch']
}) {
  const { catDims, numFields, dateFields } = FRAMES[frame]
  // A frame with no numeric columns (e.g. deal_activity) can only be counted.
  const canMeasure = numFields.length > 0
  const [dim, setDim] = useState(catDims[0])
  const [fn, setFn] = useState<AggFn>(canMeasure ? 'sum' : 'count')
  const [measure, setMeasure] = useState(numFields[0] ?? '')
  const [bucket, setBucket] = useState('quarter')
  // Running total/count only make sense over a time bucket; if the dim isn't a
  // date, a sticky cum* selection degrades to its plain agg (sum / count).
  const overTime = isDate(dim, frame)
  const effFn: AggFn = !overTime
    ? fn === 'cumsum'
      ? 'sum'
      : fn === 'cumcount'
        ? 'count'
        : fn
    : fn
  // Distinct counts a category dim's unique values; sum/avg roll up a numeric
  // column; count / running count need no field. So the "of" field set follows
  // the chosen agg.
  const measFields = effFn === 'distinct' ? catDims : numFields
  const effMeasure = measFields.includes(measure)
    ? measure
    : (measFields[0] ?? '')

  const apply = () => {
    dispatch({
      t: 'ADD_OP',
      op: { type: 'groupby', dim, aggFn: effFn, measure: effMeasure, bucket },
    })
  }

  return (
    <Wrap heading="groupby + aggregate">
      <div className={row}>
        group by
        <select
          value={dim}
          onChange={(e) => setDim(e.target.value)}
          className={ctl}
        >
          {catDims.map((d) => (
            <option key={d} value={d}>
              {d}
            </option>
          ))}
          {dateFields.map((d) => (
            <option key={d} value={d}>
              {d} (time)
            </option>
          ))}
          <option value="__none__">— none (collapse all → Metric) —</option>
        </select>
        {isDate(dim, frame) && (
          <>
            bucket
            <select
              value={bucket}
              onChange={(e) => setBucket(e.target.value)}
              className={ctl}
            >
              <option value="quarter">quarter</option>
              <option value="month">month</option>
            </select>
          </>
        )}
      </div>
      <div className={row}>
        then
        <select
          value={effFn}
          onChange={(e) => setFn(e.target.value as AggFn)}
          className={ctl}
        >
          {canMeasure && <option value="sum">sum</option>}
          {canMeasure && <option value="avg">avg</option>}
          <option value="count">count</option>
          {catDims.length > 0 && <option value="distinct">distinct</option>}
          {overTime && <option value="cumcount">running count</option>}
          {canMeasure && overTime && (
            <option value="cumsum">running total</option>
          )}
        </select>
        {effFn !== 'count' && effFn !== 'cumcount' && (
          <>
            of
            <select
              value={effMeasure}
              onChange={(e) => setMeasure(e.target.value)}
              className={ctl}
            >
              {measFields.map((f) => (
                <option key={f} value={f}>
                  {f}
                </option>
              ))}
            </select>
          </>
        )}
      </div>
      <Button variant="primary" onClick={apply}>
        Apply
      </Button>
    </Wrap>
  )
}

function PivotForm({
  frame,
  dispatch,
}: {
  frame: FrameId
  dispatch: Props['dispatch']
}) {
  // A self-contained 2D aggregation: pick the row axis, the column split, and
  // the measure all at once. Reads straight from the base rows (it's an
  // alternative to group-by, not a split layered on a collapsed result), so it
  // offers every dim — never the one already chosen for the other axis.
  const { catDims, numFields, dateFields } = FRAMES[frame]
  const canMeasure = numFields.length > 0
  const rowDims = [...catDims, ...dateFields]
  const [rowDim, setRowDim] = useState(rowDims[0] ?? '')
  const [bucket, setBucket] = useState('quarter')
  const colDims = catDims.filter((d) => d !== rowDim)
  const [colDim, setColDim] = useState(colDims[0] ?? '')
  const effColDim = colDims.includes(colDim) ? colDim : (colDims[0] ?? '')
  const [fn, setFn] = useState<AggFn>(canMeasure ? 'sum' : 'count')
  const [measure, setMeasure] = useState(numFields[0] ?? '')
  // A time row axis IS an ordered axis to accumulate along — so over time a pivot
  // can run a cumulative total/count, which the engine accumulates *per column
  // series* (one running line per split). Off a date axis a sticky cum* selection
  // degrades to its plain agg (sum / count).
  const overTime = isDate(rowDim, frame)
  const effFn: AggFn = !overTime
    ? fn === 'cumsum'
      ? 'sum'
      : fn === 'cumcount'
        ? 'count'
        : fn
    : fn
  // Distinct counts a category dim's unique values; sum/avg/running-total roll up
  // a numeric column; count / running count need no field.
  const measFields = effFn === 'distinct' ? catDims : numFields
  const effMeasure = measFields.includes(measure)
    ? measure
    : (measFields[0] ?? '')

  return (
    <Wrap heading="pivot — rows × columns, one measure per cell">
      <div className={row}>
        rows
        <select
          value={rowDim}
          onChange={(e) => setRowDim(e.target.value)}
          className={ctl}
        >
          {catDims.map((d) => (
            <option key={d} value={d}>
              {d}
            </option>
          ))}
          {dateFields.map((d) => (
            <option key={d} value={d}>
              {d} (time)
            </option>
          ))}
        </select>
        {isDate(rowDim, frame) && (
          <>
            bucket
            <select
              value={bucket}
              onChange={(e) => setBucket(e.target.value)}
              className={ctl}
            >
              <option value="quarter">quarter</option>
              <option value="month">month</option>
            </select>
          </>
        )}
      </div>
      <div className={row}>
        columns
        <select
          value={effColDim}
          onChange={(e) => setColDim(e.target.value)}
          className={ctl}
        >
          {colDims.map((d) => (
            <option key={d} value={d}>
              {d}
            </option>
          ))}
        </select>
      </div>
      <div className={row}>
        measure
        <select
          value={effFn}
          onChange={(e) => setFn(e.target.value as AggFn)}
          className={ctl}
        >
          {canMeasure && <option value="sum">sum</option>}
          {canMeasure && <option value="avg">avg</option>}
          <option value="count">count</option>
          {catDims.length > 0 && <option value="distinct">distinct</option>}
          {overTime && <option value="cumcount">running count</option>}
          {canMeasure && overTime && (
            <option value="cumsum">running total</option>
          )}
        </select>
        {effFn !== 'count' && effFn !== 'cumcount' && (
          <>
            of
            <select
              value={effMeasure}
              onChange={(e) => setMeasure(e.target.value)}
              className={ctl}
            >
              {measFields.map((f) => (
                <option key={f} value={f}>
                  {f}
                </option>
              ))}
            </select>
          </>
        )}
      </div>
      <Button
        variant="primary"
        onClick={() =>
          rowDim &&
          effColDim &&
          dispatch({
            t: 'ADD_OP',
            op: {
              type: 'pivot',
              rowDim,
              bucket,
              colDim: effColDim,
              aggFn: effFn,
              measure:
                effFn === 'count' || effFn === 'cumcount' ? '' : effMeasure,
            },
          })
        }
      >
        Apply pivot
      </Button>
    </Wrap>
  )
}

function SortForm({
  ops,
  at,
  frame,
  dispatch,
}: {
  ops: Props['ops']
  at: number | null
  frame: FrameId
  dispatch: Props['dispatch']
}) {
  const fields = schemaAt(ops, frame, at ?? undefined)
  const [field, setField] = useState(fields[0]?.name ?? '')
  const [dir, setDir] = useState<'asc' | 'desc'>('desc')
  return (
    <Wrap heading="sort">
      <div className={row}>
        <select
          value={field}
          onChange={(e) => setField(e.target.value)}
          className={ctl}
        >
          {fields.map((f) => (
            <option key={f.name} value={f.name}>
              {f.name}
            </option>
          ))}
        </select>
        <select
          value={dir}
          onChange={(e) => setDir(e.target.value as 'asc' | 'desc')}
          className={ctl}
        >
          <option value="desc">descending</option>
          <option value="asc">ascending</option>
        </select>
      </div>
      <Button
        variant="primary"
        onClick={() =>
          dispatch({ t: 'ADD_OP', op: { type: 'sort', field, dir } })
        }
      >
        Apply
      </Button>
    </Wrap>
  )
}

function LimitForm({ dispatch }: { dispatch: Props['dispatch'] }) {
  const [n, setN] = useState('10')
  return (
    <Wrap heading="limit">
      <div className={row}>
        keep first
        <input
          type="number"
          value={n}
          onChange={(e) => setN(e.target.value)}
          className={`${ctl} w-20`}
        />
        rows
      </div>
      <Button
        variant="primary"
        onClick={() =>
          dispatch({ t: 'ADD_OP', op: { type: 'limit', n: Number(n) || 10 } })
        }
      >
        Apply
      </Button>
    </Wrap>
  )
}

function SelectForm({
  ops,
  at,
  frame,
  dispatch,
}: {
  ops: Props['ops']
  at: number | null
  frame: FrameId
  dispatch: Props['dispatch']
}) {
  const cols = schemaAt(ops, frame, at ?? undefined).map((f) => f.name)
  const [kept, setKept] = useState<string[]>(cols)
  return (
    <Wrap heading="select — keep / drop columns">
      <div className={row}>
        {cols.map((c) => (
          <label key={c} className="inline-flex items-center gap-1">
            <input
              type="checkbox"
              checked={kept.includes(c)}
              onChange={(e) =>
                setKept((k) =>
                  e.target.checked ? [...k, c] : k.filter((x) => x !== c)
                )
              }
            />
            {c}
          </label>
        ))}
      </div>
      <Button
        variant="primary"
        onClick={() =>
          kept.length &&
          dispatch({ t: 'ADD_OP', op: { type: 'select', cols: kept } })
        }
      >
        Apply select
      </Button>
    </Wrap>
  )
}

function AggForm({
  ops,
  at,
  frame,
  dispatch,
}: {
  ops: Props['ops']
  at: number | null
  frame: FrameId
  dispatch: Props['dispatch']
}) {
  const fields = schemaAt(ops, frame, at ?? undefined)
  const numCols = fields.filter((f) => f.type === 'num').map((f) => f.name)
  const catCols = fields.filter((f) => f.type === 'cat').map((f) => f.name)
  const measOpts: string[] = numCols.length ? numCols : FRAMES[frame].numFields
  // Distinct counts unique values of a category column; sum/avg roll up a
  // numeric one. Fall back to the frame's dims if the live schema names none.
  const distinctOpts: string[] = catCols.length
    ? catCols
    : FRAMES[frame].catDims
  const [fn, setFn] = useState<AggFn>(measOpts.length ? 'sum' : 'count')
  const [measure, setMeasure] = useState(measOpts[0] ?? '')
  const ofOpts = fn === 'distinct' ? distinctOpts : measOpts
  const effMeasure = ofOpts.includes(measure) ? measure : (ofOpts[0] ?? '')
  const fnChoices: AggFn[] = measOpts.length
    ? ['sum', 'avg', 'count']
    : ['count']
  if (distinctOpts.length) fnChoices.push('distinct')
  return (
    <Wrap heading="aggregate — collapse the whole frame to one cell → Metric">
      <div className={row}>
        <select
          value={fn}
          onChange={(e) => setFn(e.target.value as AggFn)}
          className={ctl}
        >
          {fnChoices.map((f) => (
            <option key={f} value={f}>
              {f}
            </option>
          ))}
        </select>
        {fn !== 'count' && (
          <>
            of
            <select
              value={effMeasure}
              onChange={(e) => setMeasure(e.target.value)}
              className={ctl}
            >
              {ofOpts.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          </>
        )}
      </div>
      <Button
        variant="primary"
        onClick={() =>
          dispatch({
            t: 'ADD_OP',
            op: {
              type: 'agg',
              aggFn: fn,
              measure: fn === 'count' ? null : effMeasure,
            },
          })
        }
      >
        Apply
      </Button>
    </Wrap>
  )
}

export function OpConfig({ op, ops, insertAt, frame, dispatch }: Props) {
  const at = insertAt
  if (op === 'filter')
    return <FilterForm ops={ops} at={at} frame={frame} dispatch={dispatch} />
  if (op === 'groupby') return <GroupForm frame={frame} dispatch={dispatch} />
  if (op === 'pivot') return <PivotForm frame={frame} dispatch={dispatch} />
  if (op === 'sort')
    return <SortForm ops={ops} at={at} frame={frame} dispatch={dispatch} />
  if (op === 'limit') return <LimitForm dispatch={dispatch} />
  if (op === 'select')
    return <SelectForm ops={ops} at={at} frame={frame} dispatch={dispatch} />
  return <AggForm ops={ops} at={at} frame={frame} dispatch={dispatch} />
}
